import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

import UpcomingClasses from './UpcomingClasses';

export default function ParentDashboard() {
  const [childInfo, setChildInfo] = useState({ name: "Loading...", email: "", totalFees: 0, feesPaid: 0, batchIdStr: "" });
  const [childHistory, setChildHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedStudentEmail, setSelectedStudentEmail] = useState("");
  
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  
  const user = JSON.parse(localStorage.getItem('user') || "{}");
  const role = user?.role?.toLowerCase() || "";
  const isInstitute = role === 'institute' || role === 'administrator' || role === 'superadmin';

  const fetchChildHistory = useCallback((email) => {
    if (!email) return;
    setHistoryLoading(true);
    axios.get(`${API_BASE_URL}/api/students/email/${email}/fee-records`)
      .then(res => setChildHistory(res.data))
      .catch(console.error)
      .finally(() => setHistoryLoading(false));
  }, []);

  const fetchStudentData = useCallback((email) => {
    if (!email) return;
    axios.get(`${API_BASE_URL}/api/students/profile?email=${email}`)
      .then(res => {
          setChildInfo(res.data);
          fetchChildHistory(email);
          fetchInvoices(email);
          fetchInstallments(email);
      })
      .catch(console.error);
  }, [fetchChildHistory]);

  const fetchInvoices = (email) => {
    axios.get(`${API_BASE_URL}/api/payments/invoices/${email}`)
      .then(res => setInvoices(res.data))
      .catch(console.error);
  };

  const fetchInstallments = (email) => {
    axios.get(`${API_BASE_URL}/api/students/installments/${email}`)
      .then(res => setInstallments(res.data))
      .catch(console.error);
  };

  useEffect(() => {
    if (isInstitute) {
        // Fetch batches for filter
        const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
        setLoadingBatches(true);
        axios.get(`${API_BASE_URL}/api/batches`, { headers })
            .then(res => setBatches(res.data))
            .catch(console.error)
            .finally(() => setLoadingBatches(false));
    } else {
        // Logged in as Student - auto load self
        const email = user?.email || "arjun@example.com";
        fetchStudentData(email);
    }

    // Fetch Tenant Info for Razorpay Key
    axios.get(`${API_BASE_URL}/api/theme`)
        .then(res => setTenantInfo(res.data))
        .catch(console.error);

    // Initial history fetch for default mock or student
    fetchChildHistory(childInfo.email);
  }, [isInstitute, user?.email, user?.tenantEmail, childInfo.email, fetchStudentData, fetchChildHistory]);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayNow = async () => {
    const amount = childInfo.totalFees - childInfo.feesPaid;
    if (amount <= 0) return;

    if (!tenantInfo?.razorpayKeyId) {
      alert("Online payment is not configured for this institute. Please contact administration.");
      return;
    }

    setProcessingPayment(true);
    const res = await loadRazorpay();
    if (!res) {
      alert("Razorpay SDK failed to load. Check your internet connection.");
      setProcessingPayment(false);
      return;
    }

    try {
      // Create Order
      const { data: order } = await axios.post(`${API_BASE_URL}/api/payments/create-order`, {
        amount,
        studentEmail: childInfo.email
      });

      const options = {
        key: tenantInfo.razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: tenantInfo.schoolName || "PenSense Institute",
        description: `Fee Payment for ${childInfo.name}`,
        order_id: order.id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API_BASE_URL}/api/payments/verify`, response);
            if (verifyRes.data.status === 'success') {
              alert("Payment Successful! Invoice generated.");
              fetchStudentData(childInfo.email);
            }
          } catch (e) {
            console.error(e);
            alert("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: childInfo.name,
          email: childInfo.email,
        },
        theme: {
          color: tenantInfo.themeColor || "#3b82f6"
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (e) {
      console.error(e);
      alert("Failed to initiate payment. " + (e.response?.data?.message || ""));
    } finally {
      setProcessingPayment(false);
    }
  };

  const calculateAttendance = () => {
    if (!childInfo) return { percent: 0, attended: 0, missed: 0, total: 0 };
    const records = childInfo.attendanceRecords || [];
    const today = new Date();
    const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();
    const isFutureMonth = viewDate.getFullYear() > today.getFullYear() || (viewDate.getFullYear() === today.getFullYear() && viewDate.getMonth() > today.getMonth());
    
    let daysPassedInMonth = 0;
    if (isCurrentMonth) {
      daysPassedInMonth = today.getDate();
    } else if (!isFutureMonth) {
      daysPassedInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    }
    
    const presentInMonth = records.filter(r => {
      if (r.status !== 'present') return false;
      const dateParts = r.date.split('-');
      if (dateParts.length < 3) return false;
      const rYear = parseInt(dateParts[0], 10);
      const rMonth = parseInt(dateParts[1], 10) - 1;
      return rYear === viewDate.getFullYear() && rMonth === viewDate.getMonth();
    }).length;

    const missedInMonth = records.filter(r => {
      if (r.status !== 'absent') return false;
      const dateParts = r.date.split('-');
      if (dateParts.length < 3) return false;
      const rYear = parseInt(dateParts[0], 10);
      const rMonth = parseInt(dateParts[1], 10) - 1;
      return rYear === viewDate.getFullYear() && rMonth === viewDate.getMonth();
    }).length;

    const percent = (daysPassedInMonth === 0 || isFutureMonth) ? 0 : Math.round((presentInMonth / daysPassedInMonth) * 100);
    
    return { percent, attended: presentInMonth, missed: missedInMonth, total: daysPassedInMonth };
  };

  const { percent, attended, missed } = calculateAttendance();

  const getDaysInMonth = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const numDays = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: numDays }, (_, i) => {
      const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
      let status = 'none';
      let tooltip = '';
      
      const record = childInfo.attendanceRecords?.find(r => r.date === dayStr);
      if (record) {
        status = record.status;
        tooltip = `${record.status === 'present' ? 'Present' : 'Absent'}${record.timeIn ? `\nIn: ${record.timeIn}` : ''}${record.timeOut ? `\nOut: ${record.timeOut}` : ''}${record.remarks ? `\nNote: ${record.remarks}` : ''}`;
      } else {
        if (childInfo.presentDays?.includes(dayStr)) status = 'present';
        if (childInfo.absentDays?.includes(dayStr)) status = 'absent';
      }
      
      return { day: i + 1, status, tooltip };
    });
  };

  const getFirstDayOfMonth = () => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      return firstDay === 0 ? 6 : firstDay - 1;
  };

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

  const calculateStreak = () => {
    if (!childInfo.attendanceRecords || childInfo.attendanceRecords.length === 0) return 0;
    const sorted = [...childInfo.attendanceRecords].sort((a, b) => b.date.localeCompare(a.date));
    let streak = 0;
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].status === 'present') streak++;
      else if (sorted[i].status === 'absent') break;
    }
    return streak;
  };

  const calculateAvgScore = () => {
    if (!childInfo.testResults || childInfo.testResults.length === 0) return "N/A";
    const totalObtained = childInfo.testResults.reduce((acc, r) => acc + (r.physicsScore || 0) + (r.chemScore || 0) + (r.mathScore || 0) + (r.bioScore || 0), 0);
    const totalMax = childInfo.testResults.reduce((acc, r) => acc + (r.physicsMax || 0) + (r.chemMax || 0) + (r.mathMax || 0) + (r.bioMax || 0), 0);
    if (totalMax === 0) return "N/A";
    return `${Math.round(totalObtained)}/${Math.round(totalMax)}`;
  };

  const DYNAMIC_METRICS = [
    { label: "Overall Attendance", value: `${percent}%`, trend: percent >= 75 ? "Good" : "Needs Work", color: percent >= 75 ? "text-emerald-400" : "text-rose-400", bg: "bg-emerald-500/10", clickable: true },
    { label: "Study Consistency", value: calculateStreak() > 5 ? "High" : "Average", trend: `${calculateStreak()} day streak`, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Avg Mock Score", value: calculateAvgScore(), trend: childInfo.testResults?.length > 1 ? "Across all tests" : "Latest test", color: "text-purple-400", bg: "bg-purple-500/10" },
    { label: "Assignments Done", value: `${childInfo.tasks?.filter(t => t.completed).length || 0}/${childInfo.tasks?.length || 0}`, trend: "On track", color: "text-sky-400", bg: "bg-sky-500/10" },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
      
      {/* Header Profile Section */}
      <div className="rounded-3xl bg-gradient-to-r from-sky-900/40 via-indigo-900/20 to-slate-900 border border-sky-500/20 p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 blur-[80px] rounded-full"></div>
        <div className="absolute bottom-0 left-32 w-48 h-48 bg-indigo-500/10 blur-[60px] rounded-full"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-full bg-slate-800 border-2 border-sky-400/50 flex items-center justify-center text-2xl overflow-hidden shrink-0">
               👨‍👩‍👧
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white mb-1">
                Parent Portal
              </h1>
              <p className="text-sm text-sky-200/70 font-medium">
                Tracking: <span className="text-white font-semibold">{childInfo.name}</span> • {childInfo.batchIdStr || 'Class 11 (JEE Batch 2025)'}
              </p>
            </div>
          </div>
          
          <button className="px-5 py-2 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/30 font-medium transition-colors backdrop-blur-sm flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            Message Mentor
          </button>
        </div>
      </div>

      {/* Role-Based Filter Section */}
      {isInstitute && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Search Scope:</span>
                 <select 
                    value={selectedBatch}
                    onChange={(e) => {
                        const bId = e.target.value;
                        setSelectedBatch(bId);
                        if (bId) {
                            setLoadingStudents(true);
                            axios.get(`${API_BASE_URL}/api/students/by-batch/${bId}`)
                                .then(res => setStudents(res.data))
                                .catch(console.error)
                                .finally(() => setLoadingStudents(false));
                        } else {
                            setStudents([]);
                        }
                    }}
                    className="w-full md:w-64 bg-slate-900/80 border border-sky-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-sky-500 transition-colors shadow-inner font-medium"
                >
                    <option value="">{loadingBatches ? "Loading Batches..." : "Select Batch..."}</option>
                    {batches.map(b => <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>)}
                </select>
                
                <select 
                    value={selectedStudentEmail}
                    onChange={(e) => {
                        setSelectedStudentEmail(e.target.value);
                        fetchStudentData(e.target.value);
                    }}
                    disabled={!selectedBatch || loadingStudents}
                    className="w-full md:w-80 bg-slate-900/80 border border-sky-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-sky-500 transition-colors shadow-inner font-medium disabled:opacity-50"
                >
                    <option value="">
                        {!selectedBatch 
                            ? "Select Batch First..." 
                            : loadingStudents 
                                ? "Loading Students..." 
                                : "Select Student..."}
                    </option>
                    {students.map(s => <option key={s.id} value={s.email}>{s.name} ({s.email})</option>)}
                </select>
            </div>
            {selectedStudentEmail && (
                <div className="flex items-center gap-2 text-sky-400 text-xs font-medium animate-pulse">
                    <div className="w-1.5 h-1.5 rounded-full bg-sky-500"></div>
                    Dynamic View Synchronized
                </div>
            )}
        </div>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {DYNAMIC_METRICS.map((metric, idx) => (
          <div 
            key={idx} 
            className={`rounded-2xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col justify-between hover:bg-slate-800/40 transition-colors group ${metric.clickable ? 'cursor-pointer hover:border-sky-500/50' : ''}`}
            onClick={() => metric.clickable && setShowAttendanceModal(true)}
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{metric.label}</p>
            <div>
              <h3 className={`text-3xl font-bold ${metric.color} mb-1 group-hover:scale-105 transform origin-left transition-transform duration-300`}>{metric.value}</h3>
              <p className="text-xs text-slate-400">{metric.trend}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Performance & Alerts */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Recent Performance Tracker */}
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-[50px] rounded-full"></div>
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
                <span className="text-purple-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
                </span>
                Recent Test Performance
              </h2>
              <button className="text-xs font-medium text-purple-400 hover:text-purple-300">Detailed Report</button>
            </div>
            
            <div className="space-y-4 relative z-10">
              {(!childInfo.testResults || childInfo.testResults.length === 0) ? (
                <div className="py-8 text-center text-slate-500 italic text-sm border border-dashed border-slate-800 rounded-2xl">
                  No recent test performance records found for this student.
                </div>
              ) : (
                childInfo.testResults.slice().reverse().slice(0, 3).map((test, idx) => {
                  const scoreTotal = (test.physicsScore || 0) + (test.chemScore || 0) + (test.mathScore || 0) + (test.bioScore || 0);
                  const maxTotal = (test.physicsMax || 0) + (test.chemMax || 0) + (test.mathMax || 0) + (test.bioMax || 0);
                  const percentage = maxTotal > 0 ? (scoreTotal / maxTotal) * 100 : 0;
                  const status = percentage >= 85 ? 'Excellent' : percentage >= 60 ? 'Good' : 'Needs Improvement';
                  
                  return (
                    <div key={idx} className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-200">{test.testName}</h3>
                        <p className="text-xs text-slate-400 mt-1">{test.date}</p>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="block text-[10px] uppercase text-slate-500 font-semibold mb-1">Score</span>
                          <span className={`text-lg font-bold ${status === 'Needs Improvement' ? 'text-rose-400' : 'text-emerald-400'}`}>
                            {Math.round(scoreTotal)}/{Math.round(maxTotal)}
                          </span>
                        </div>
                        <div className="text-right hidden sm:block">
                          <span className="block text-[10px] uppercase text-slate-500 font-semibold mb-1">Percentage</span>
                          <span className="text-lg font-bold text-slate-300">{Math.round(percentage)}%</span>
                        </div>
                        <div>
                          <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded inline-block w-24 text-center ${
                            status === 'Needs Improvement' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 
                            status === 'Excellent' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                            'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          }`}>
                            {status}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Critical Alerts Dashboard */}
          <section className={`rounded-3xl border p-6 transition-all duration-500 ${!childInfo.testResults || childInfo.testResults.length === 0 || ((childInfo.testResults[childInfo.testResults.length-1].physicsScore || 0) + (childInfo.testResults[childInfo.testResults.length-1].chemScore || 0)) / ((childInfo.testResults[childInfo.testResults.length-1].physicsMax || 1) + (childInfo.testResults[childInfo.testResults.length-1].chemMax || 0)) > 0.5 ? 'border-sky-500/20 bg-sky-950/20' : 'border-rose-500/20 bg-rose-950/20'}`}>
            <div className="flex items-center gap-2 mb-6">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full ${!childInfo.testResults || childInfo.testResults.length === 0 || ((childInfo.testResults[childInfo.testResults.length-1].physicsScore || 0) + (childInfo.testResults[childInfo.testResults.length-1].chemScore || 0)) / ((childInfo.testResults[childInfo.testResults.length-1].physicsMax || 1) + (childInfo.testResults[childInfo.testResults.length-1].chemMax || 0)) > 0.5 ? 'bg-sky-500/20 text-sky-400' : 'bg-rose-500/20 text-rose-400'}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              </span>
              <h2 className={`text-lg font-semibold ${!childInfo.testResults || childInfo.testResults.length === 0 || ((childInfo.testResults[childInfo.testResults.length-1].physicsScore || 0) + (childInfo.testResults[childInfo.testResults.length-1].chemScore || 0)) / ((childInfo.testResults[childInfo.testResults.length-1].physicsMax || 1) + (childInfo.testResults[childInfo.testResults.length-1].chemMax || 0)) > 0.5 ? 'text-sky-200' : 'text-rose-200'}`}>
                Performance Status
              </h2>
            </div>
            
            {(!childInfo.testResults || childInfo.testResults.length === 0) ? (
              <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 backdrop-blur flex gap-4 items-start">
                 <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 shrink-0 mt-0.5">
                   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                 </div>
                 <div>
                    <h3 className="font-medium text-slate-200 text-sm">Steady Progress</h3>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">Arjun is consistently attending all scheduled live sessions. No critical academic alerts have been flagged this week.</p>
                 </div>
              </div>
            ) : (() => {
               const latest = childInfo.testResults[childInfo.testResults.length - 1];
               const score = (latest.physicsScore || 0) + (latest.chemScore || 0) + (latest.mathScore || 0);
               const max = (latest.physicsMax || 0) + (latest.chemMax || 0) + (latest.mathMax || 0);
               const perc = max > 0 ? (score / max) * 100 : 0;
               
               if (perc < 50) {
                 return (
                  <div className="p-4 rounded-xl bg-slate-900/60 border border-rose-500/10 backdrop-blur flex gap-4 items-start animate-in pulse duration-[2s] repeat-infinite">
                    <div className="p-2 rounded-lg bg-rose-500/10 text-rose-400 shrink-0 mt-0.5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-200 text-sm">Low Score in {latest.testName}</h3>
                      <p className="text-xs text-slate-400 mt-1 mb-3 leading-relaxed">{childInfo.name} scored {Math.round(perc)}% on the recent test. We recommend identifying the weak modules and reviewing related video lectures.</p>
                      <button className="text-xs font-semibold text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-1.5 rounded transition-colors">Acknowledge</button>
                    </div>
                  </div>
                 );
               } else {
                 return (
                  <div className="p-4 rounded-xl bg-slate-900/60 border border-emerald-500/10 backdrop-blur flex gap-4 items-start">
                    <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 shrink-0 mt-0.5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-200 text-sm">Performing Well</h3>
                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">{childInfo.name} is showing consistent performance with {Math.round(perc)}% in the latest assessment. Keep up the good work!</p>
                    </div>
                  </div>
                 );
               }
            })()}
          </section>

        </div>

        {/* Right Column: Attendance & Quick Contact */}
        <div className="space-y-8">
          
          {/* Real-time Fee Status Progress */}
          <section className="rounded-3xl border border-emerald-500/20 bg-gradient-to-b from-emerald-900/20 to-slate-900/40 p-6 relative overflow-hidden text-left">
            <div className="absolute top-0 right-0 w-full h-32 bg-emerald-500/10 blur-[50px]"></div>
            
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h2 className="text-sm font-bold uppercase tracking-widest text-emerald-400">Tuition & Fee Status</h2>
              <div className="bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                <span className="text-[10px] font-bold text-emerald-400">LIVE SYNC</span>
              </div>
            </div>
            
            <div className="space-y-4 relative z-10">
               {/* Metrics */}
               <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800">
                    <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Total Fee</p>
                    <p className="text-lg font-bold text-white">₹{(childInfo?.totalFees ?? 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800">
                    <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Paid</p>
                    <p className="text-lg font-bold text-emerald-400">₹{(childInfo?.feesPaid ?? 0).toLocaleString('en-IN')}</p>
                  </div>
               </div>

               {/* Overdue Calculation */}
               {(() => {
                 const today = new Date().toISOString().split('T')[0];
                 const dueToday = childInfo.totalDueToDate || 0;
                 
                 const overdue = Math.max(0, dueToday - (childInfo.feesPaid || 0));
                 const nextInst = (installments || [])
                   .filter(inst => inst.dueDate > today)
                   .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];

                 return (
                   <>
                     {overdue > 0 && (
                       <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-between animate-in pulse duration-[2s] repeat-infinite">
                          <div>
                            <p className="text-[10px] text-rose-400 font-bold uppercase">Total Overdue</p>
                            <p className="text-xl font-black text-rose-400">
                              ₹{(overdue ?? 0).toLocaleString('en-IN')}
                            </p>
                          </div>
                           <button 
                              onClick={handlePayNow}
                              disabled={processingPayment}
                              className="p-2 px-4 rounded-xl bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 transition-colors shadow-lg shadow-rose-500/20 disabled:opacity-50"
                           >
                              {processingPayment ? "Working..." : "Pay Now"}
                           </button>
                       </div>
                     )}

                     {nextInst && (
                       <div className="p-4 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-sky-400 font-bold uppercase">Next Installment</p>
                            <p className="text-sm font-bold text-white">
                              ₹{(nextInst.amount ?? 0).toLocaleString('en-IN')} due on {nextInst.dueDate}
                            </p>
                          </div>
                       </div>
                     )}
                   </>
                 );
               })()}

               {/* Installment Schedule List */}
               {installments && installments.length > 0 && (
                 <div className="mt-4 pt-4 border-t border-slate-800">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Payment Schedule</h3>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                       {installments.sort((a, b) => a.dueDate.localeCompare(b.dueDate)).map((inst, i) => {
                         const today = new Date().toISOString().split('T')[0];
                         const isOverdue = inst.dueDate < today;
                         
                         return (
                           <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/50">
                              <div className="flex flex-col">
                                 <span className="text-xs font-bold text-slate-200">₹{(inst.amount ?? 0).toLocaleString('en-IN')}</span>
                                 <span className={`text-[9px] font-bold ${isOverdue ? 'text-rose-400' : 'text-slate-500'}`}>
                                   {isOverdue ? 'OVERDUE' : 'UPCOMING'}
                                 </span>
                              </div>
                              <span className="text-[9px] font-mono text-slate-400">{inst.dueDate}</span>
                           </div>
                         );
                       })}
                    </div>
                 </div>
               )}

               {/* Transaction History Sub-list */}
               <div className="mt-4 pt-4 border-t border-slate-800">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Payment Log</h3>
                  <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                     {historyLoading ? (
                       <div className="py-4 text-center text-xs text-slate-500 animate-pulse">Fetching history...</div>
                     ) : childHistory.length === 0 ? (
                       <div className="py-4 text-center text-xs text-slate-600 italic">No historical records found.</div>
                     ) : (
                       childHistory.slice().reverse().map((rec, i) => {
                          // Try to find matching invoice for this payment date/amount
                          // Note: In real app, match by paymentId or link FeeRecord to Invoice
                          const inv = invoices.find(v => Math.abs(v.totalAmount - (rec.amountPaid || 0)) < 1);
                          
                          return (
                            <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/50">
                               <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                     <span className="text-xs font-bold text-slate-200">₹{(rec.amountPaid || 0).toLocaleString('en-IN')}</span>
                                     {inv && (
                                       <a 
                                         href={`${API_BASE_URL}/api/payments/invoice/download/${inv.id}`} 
                                         target="_blank" 
                                         rel="noreferrer"
                                         className="p-1 rounded bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-colors"
                                         title="Download GST Invoice"
                                       >
                                         <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                                       </a>
                                     )}
                                  </div>
                                  <span className="text-[9px] text-slate-500 font-medium truncate w-24">#{rec.remarks || 'Standard'}</span>
                               </div>
                               <span className="text-[9px] font-mono text-emerald-500/70">{rec.paymentDate || 'N/A'}</span>
                            </div>
                          );
                        })
                     )}
                  </div>
               </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 text-center overflow-hidden">
            <h2 className="text-lg font-semibold text-slate-200 mb-6">Attendance Overview</h2>
            
            <div className="relative w-32 h-32 mx-auto mb-6">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="#1e293b" strokeWidth="10" />
                <circle cx="50" cy="50" r="45" fill="none" stroke={percent >= 75 ? "#10b981" : "#f43f5e"} strokeWidth="10" strokeDasharray="283" strokeDashoffset={283 - (283 * (percent / 100))} className="transition-all duration-1000 ease-out" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-3xl font-bold text-white">{percent}%</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4 bg-slate-800/30 p-4 rounded-2xl">
              <div>
                <span className="block text-xl font-bold text-emerald-400">{attended}</span>
                <span className="text-[10px] uppercase font-semibold text-slate-500">Classes Attended</span>
              </div>
              <div>
                <span className="block text-xl font-bold text-rose-400">{missed}</span>
                <span className="text-[10px] uppercase font-semibold text-slate-500">Classes Missed</span>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-1 relative overflow-hidden">
            <div className="p-5 border-b border-slate-800/50">
               <h3 className="text-sm font-semibold text-indigo-300 uppercase tracking-widest">Live Schedule</h3>
            </div>
            <UpcomingClasses hideHeader={true} initialBatch={childInfo.batchIdStr} />
          </section>

        </div>

      </div>
      {/* Attendance Log Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
           <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
              <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                <div>
                  <h2 className="text-xl font-bold text-white">Detailed Attendance Log</h2>
                  <p className="text-sm text-slate-400">Viewing history for {childInfo.name}</p>
                </div>
                <button 
                  onClick={() => setShowAttendanceModal(false)}
                  className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              
              <div className="p-8">
                {/* Month Navigator */}
                <div className="flex items-center justify-between mb-8 bg-slate-950/40 p-4 rounded-2xl border border-slate-800/50">
                  <button onClick={handlePrevMonth} className="p-2 hover:bg-slate-800 rounded-xl text-sky-400 transition-colors">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  <h3 className="text-lg font-bold text-white capitalize">
                    {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button onClick={handleNextMonth} className="p-2 hover:bg-slate-800 rounded-xl text-sky-400 transition-colors">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>

                {/* Calendar Heatmap */}
                <div className="grid grid-cols-7 gap-3">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="text-center text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-2">{day}</div>
                  ))}
                  
                  {Array.from({ length: getFirstDayOfMonth() }).map((_, i) => (
                    <div key={`emp-${i}`} className="aspect-[4/3]"></div>
                  ))}
                  
                  {getDaysInMonth().map((d, i) => {
                    let colorClass = "bg-slate-800/20 border-slate-800 text-slate-600";
                    if (d.status === 'present') colorClass = "bg-emerald-500/20 border-emerald-500/50 text-emerald-400 font-bold shadow-[0_0_15px_rgba(16,185,129,0.1)]";
                    if (d.status === 'absent') colorClass = "bg-rose-500/20 border-rose-500/50 text-rose-400 font-bold shadow-[0_0_15px_rgba(244,63,94,0.1)]";
                    
                    return (
                      <div 
                        key={i} 
                        title={d.tooltip}
                        className={`aspect-[4/3] rounded-xl border flex items-center justify-center text-sm transition-all hover:scale-110 hover:bg-slate-800 cursor-help ${colorClass}`}
                      >
                        {d.day}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex items-center justify-center gap-6 text-[10px] font-bold uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50"></div>
                    <span className="text-emerald-400/80">Present</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-rose-500/30 border border-rose-500/50"></div>
                    <span className="text-rose-400/80">Absent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-slate-800/30 border border-slate-800"></div>
                    <span className="text-slate-500">No Record</span>
                  </div>
                </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
}
